import { breachedPasswordMessage, checkPasswordBreached } from '@auctisync/shared';
import { Button } from '@auctisync/ui';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AuthSplit } from '../../components/bidder-web';
import { useSession } from '../../hooks/useSession';
import { authRedirectUrl } from '../../lib/authRedirect';
import { sha1Hex } from '../../lib/sha1';
import { supabase } from '../../lib/supabase';

const MIN_PASSWORD = 8;

// 'checking' is the breach lookup, which happens BEFORE the account is created.
// It gets its own label so a slow HaveIBeenPwned response can't leave the user
// reading "Creating account…" for seconds and believing an account now exists.
type Mode = 'idle' | 'checking' | 'submitting' | 'check-email';

/**
 * Bidder self-service sign-up (shared app, app.auctisync.com + iOS/Android).
 *
 * Email + password + confirm-password. On submit we call `supabase.auth.signUp`.
 * If the project requires email confirmation (it does), Supabase returns no
 * session and emails a confirm link → we show the "check your email" state; the
 * link lands on `/auth/callback?next=/` (AuthCallback finalizes the session and
 * forwards to BidderHome). If confirmation is ever turned off, signUp returns a
 * session and we route straight to `/`.
 */
export function BidderSignUp() {
  const navigate = useNavigate();
  const { session, loading } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);

  // Already signed in? Nothing to create — send them into the app.
  if (!loading && session) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match. Please re-enter them.');
      return;
    }

    // Reject passwords already circulating in breach corpora, BEFORE creating
    // the account — the whole point is that this password never becomes the
    // credential. Fails open: if HaveIBeenPwned is unreachable the sign-up
    // proceeds rather than the outage blocking all new accounts.
    setMode('checking');
    const breach = await checkPasswordBreached(password, sha1Hex);
    if (breach.breached) {
      setError(breachedPasswordMessage(breach.count));
      setMode('idle');
      return;
    }

    setMode('submitting');
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: authRedirectUrl('/auth/callback?next=/') },
    });

    if (signUpError) {
      // Friendlier message for the most common case (email already has an account).
      const msg = /already registered|already exists|already been registered/i.test(
        signUpError.message,
      )
        ? 'An account already exists for this email. Try signing in instead.'
        : signUpError.message;
      setError(msg);
      setMode('idle');
      return;
    }

    // Confirmation OFF → session present → straight into the app.
    if (data.session) {
      await navigate('/', { replace: true });
      return;
    }

    // Confirmation ON (normal) → no session yet; the confirm email is on its way.
    setMode('check-email');
  }

  const busy = mode === 'checking' || mode === 'submitting';

  return (
    <AuthSplit>
      <div>
        <h2 className="w-h2">Create your account</h2>
        <p className="w-body muted" style={{ marginTop: 8 }}>
          One account works with every house on the platform. You then register with each house
          separately — they approve their own bidders.
        </p>
      </div>
      {mode === 'check-email' ? (
        <div
          role="status"
          style={{
            padding: '1rem',
            borderRadius: '0.5rem',
            background: 'var(--surface-muted, #f5f5f5)',
            color: 'var(--text, #111)',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          <strong>Almost there — check your email.</strong> We sent a confirmation link to{' '}
          <code>{email.trim()}</code>. Tap it to confirm your email and you&apos;ll be signed in
          automatically. The link expires in 1 hour.
        </div>
      ) : (
        <>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            style={{ display: 'grid', gap: '0.75rem' }}
          >
            <label className="wa-fi" htmlFor="signup-email">
              <span className="w-lbl">Email</span>
              <input
                id="signup-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                disabled={busy}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="wa-fi" htmlFor="signup-password">
              <span className="w-lbl">Password</span>
              <input
                id="signup-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                disabled={busy}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="muted" style={{ fontSize: 12 }}>
                At least {MIN_PASSWORD} characters. A passphrase beats a short jumble.
              </span>
            </label>
            <label className="wa-fi" htmlFor="signup-confirm">
              <span className="w-lbl">Confirm password</span>
              <input
                id="signup-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                disabled={busy}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>

            {error && (
              <div
                role="alert"
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--semantic-error, #b91c1c)',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-sm, 0.375rem)',
                  background: 'var(--semantic-error-bg, #fef2f2)',
                  border: '1px solid var(--semantic-error, #b91c1c)',
                }}
              >
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy}>
              {mode === 'checking'
                ? 'Checking password…'
                : busy
                  ? 'Creating account…'
                  : 'Create account'}
            </Button>
          </form>
        </>
      )}

      <div className="wau-sep">or</div>
      {/* Watching and bidding both need an account; BROWSING does not, and
          saying so is worth more than the sign-ups a wall would buy. */}
      <Link to="/browse" className="w-btn w-btn-out w-btn-full">
        Browse without an account
      </Link>
      <p
        className="muted"
        style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55, textAlign: 'center' }}
      >
        Already have an account? <Link to="/signin">Sign in</Link>.
      </p>
    </AuthSplit>
  );
}
